import React, { useState, useContext, lazy, Suspense, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Tooltip,
  IconButton,
  Divider,
  Snackbar,
  Dialog,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useNavigate } from "react-router-dom";
import { CartContext } from "../../../contexts/CartContext";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import useBookDetails from "../../../hooks/api-request/useBookDetails";
import BookDetailsSkeleton from "../../../components/skeletons/BookDetailsSkeleton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import {
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
  Audiotrack as AudioIcon,
} from "@mui/icons-material";
import XIcon from "../../../assets/icons/XIcon";
import SEO from "../../../components/seo/SEO";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LaunchIcon from "@mui/icons-material/Launch";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

// Lazy load readers for better performance
const PDFReader = lazy(() => import("../../../components/readers/PDFReader"));
const AudioPlayer = lazy(() => import("../../../components/readers/AudioPlayer"));

const BookImage = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: "150px",
  height: "auto",
  objectFit: "cover",
  borderRadius: theme.spacing(1),
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "120px",
  },
}));

const FormatButton = styled(Button)(({ theme, active }) => ({
  width: "100%",
  justifyContent: "flex-start",
  padding: "12px 16px",
  borderColor: active ? theme.palette.primary.main : theme.palette.divider,
  backgroundColor: active ? theme.palette.primary.lighter : "transparent",
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.lighter
      : theme.palette.action.hover,
  },
}));

const BookInfoWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: theme.spacing(2),
    "& .MuiBox-root": {
      width: "100%",
    },
  },
}));

const ActionButtonsWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    width: "100%",
    "& .MuiButton-root": {
      width: "100%",
    },
  },
}));

const PriceSection = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1.5),
}));

const PriceWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const MainPrice = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 800,
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  "& .currency": {
    fontSize: "1.5rem",
    fontWeight: 600,
  },
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  color: theme.palette.text.secondary,
  textDecoration: "line-through",
  opacity: 0.7,
}));

const DiscountBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 12px",
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  fontSize: "1rem",
  fontWeight: 700,
  gap: theme.spacing(0.5),
  boxShadow: "0 2px 8px rgba(239, 68, 68, 0.25)",
}));

const StickyPurchaseBox = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 24,
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  [theme.breakpoints.down("md")]: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    top: "auto",
    zIndex: 1000,
    borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
    boxShadow: "0 -4px 12px rgba(0,0,0,0.1)",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const NotFoundWrapper = styled(Box)(({ theme }) => ({
  minHeight: "60vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
}));

const NotFoundContent = styled(Box)(({ theme }) => ({
  textAlign: "center",
  maxWidth: "400px",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
}));

const FreeBookBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 16px",
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  fontSize: "1.2rem",
  fontWeight: 700,
  gap: theme.spacing(0.5),
  boxShadow: "0 2px 8px rgba(76, 175, 80, 0.25)",
  marginBottom: theme.spacing(2),
}));

const ReadButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: theme.spacing(1),
  fontSize: "1rem",
  transition: "all 0.2s ease",
  boxShadow: "0 4px 12px rgba(76, 175, 80, 0.25)",
  "&:hover": {
    backgroundColor: theme.palette.success.dark,
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "8px 16px",
    fontSize: "0.9rem",
    height: "40px",
  },
}));

const FileSelectionDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    width: "100%",
    maxWidth: 400,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const FileList = styled(List)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const FileItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  cursor: "pointer",
}));

const YouTubeButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: theme.spacing(1),
  fontSize: "1rem",
  transition: "all 0.2s ease",
  boxShadow: "0 4px 12px rgba(244, 67, 54, 0.25)",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(244, 67, 54, 0.3)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "8px 16px",
    fontSize: "0.9rem",
    height: "40px",
  },
}));

const YouTubeListItem = styled(ListItem)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: "all 0.2s ease",
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
  },
}));

const calculateDiscount = (originalPrice, price) => {
  if (!originalPrice) return 0;
  return Math.round(
    ((parseFloat(originalPrice) - parseFloat(price)) /
      parseFloat(originalPrice)) *
    100,
  );
};

const ShareSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
}));

const ShareButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  "&.facebook:hover": {
    backgroundColor: "#1877F2",
    color: "#fff",
    borderColor: "#1877F2",
  },
  "&.x:hover": {
    backgroundColor: "#000000",
    color: "#fff",
    borderColor: "#000000",
  },
  "&.linkedin:hover": {
    backgroundColor: "#0A66C2",
    color: "#fff",
    borderColor: "#0A66C2",
  },
  "&.whatsapp:hover": {
    backgroundColor: "#25D366",
    color: "#fff",
    borderColor: "#25D366",
  },
}));

const SocialShare = ({ book }) => {
  const { t } = useTranslation();
  const [copySuccess, setCopySuccess] = useState(false);
  const currentUrl = window.location.href;

  // Create a more descriptive share message with book details
  const shareTitle = `${book.title} | Hasa Book Store`;
  const shareDescription =
    book.description.length > 100
      ? `${book.description.substring(0, 97)}...`
      : book.description;
  const shareMessage = `Check out "${book.title}" by ${book.added_by_display.join(", ")} on Hasa Book Store`;

  const shareData = {
    title: shareTitle,
    text: shareMessage,
    url: currentUrl,
  };

  const handleShare = async (platform) => {
    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
          "_blank",
        );
        break;
      case "x":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            `${shareMessage}: ${book.price ? "$" + book.price : ""}`,
          )}&url=${encodeURIComponent(currentUrl)}`,
          "_blank",
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}&summary=${encodeURIComponent(shareDescription)}`,
          "_blank",
        );
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(
            `${shareMessage}: ${currentUrl}`,
          )}`,
          "_blank",
        );
        break;
      case "copy":
        try {
          await navigator.clipboard.writeText(currentUrl);
          setCopySuccess(true);
        } catch (err) {
          console.error("Failed to copy:", err);
        }
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share(shareData);
          } catch (err) {
            console.error("Share failed:", err);
          }
        }
    }
  };

  return (
    <ShareSection>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ShareIcon color="action" />
        <Typography variant="subtitle1" fontWeight={600}>
          {t("books.details.shareBook")}
        </Typography>
      </Stack>
      <Divider sx={{ mb: 3 }} />
      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        sx={{
          gap: 2,
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        <Tooltip title={t("books.details.share.shareOnFacebook")}>
          <ShareButton
            className="facebook"
            onClick={() => handleShare("facebook")}
          >
            <FacebookIcon />
          </ShareButton>
        </Tooltip>
        <Tooltip title={t("books.details.share.shareOnX")}>
          <ShareButton
            className="x"
            onClick={() => handleShare("x")}
            sx={{
              "& svg": {
                width: "20px",
                height: "20px",
              },
            }}
          >
            <XIcon />
          </ShareButton>
        </Tooltip>
        <Tooltip title={t("books.details.share.shareOnLinkedIn")}>
          <ShareButton
            className="linkedin"
            onClick={() => handleShare("linkedin")}
          >
            <LinkedInIcon />
          </ShareButton>
        </Tooltip>
        <Tooltip title={t("books.details.share.shareOnWhatsApp")}>
          <ShareButton
            className="whatsapp"
            onClick={() => handleShare("whatsapp")}
          >
            <WhatsAppIcon />
          </ShareButton>
        </Tooltip>
        <Tooltip title={t("books.details.share.copyLink")}>
          <ShareButton onClick={() => handleShare("copy")}>
            <CopyIcon />
          </ShareButton>
        </Tooltip>
        {navigator.share && (
          <Tooltip title={t("books.details.share.moreOptions")}>
            <ShareButton onClick={() => handleShare("native")}>
              <ShareIcon />
            </ShareButton>
          </Tooltip>
        )}
      </Stack>
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message={t("books.details.share.linkCopied")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </ShareSection>
  );
};

const BookDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [selectedFormat, setSelectedFormat] = useState("eBook");
  const { addToCart, cartItems } = useContext(CartContext);
  const [isAdding, setIsAdding] = useState(false);
  const { data: book, isLoading } = useBookDetails(id);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Check if the language is RTL (Pashto or Dari)
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  // States for file selection and reader
  const [readerOpen, setReaderOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [fileSelectionOpen, setFileSelectionOpen] = useState(false);
  const [isLoadingReader, setIsLoadingReader] = useState(false);

  const isInCart = cartItems.some((item) => item.id === id);

  // Check if the book is an audio book with YouTube links
  const hasYoutubeLinks = useMemo(() => {
    if (!book || book.format !== "Audiobook") return false;
    return book.collections?.some(collection => !!collection.youtube_link);
  }, [book]);
  
  // Check if book is audio format or PDF
  const isAudio = useMemo(() => {
    if (!book) return false;
    return book.format === "Audiobook";
  }, [book]);
  
  // Check if book is purchased or free to determine UI
  const isPurchasedOrFree = useMemo(() => {
    if (!book) return false;
    return book.is_purchased || book.is_free;
  }, [book]);

  const handleAddToCart = () => {
    if (isInCart) return;

    setIsAdding(true);
    addToCart({
      id,
      title: book.title,
      price: book.price,
      image: book.cover_image,
      format: selectedFormat,
    });

    setTimeout(() => {
      setIsAdding(false);
    }, 1000);
  };

  // Function to handle reading a book or playing audio
  const handleReadOrPlay = () => {
    if (book.collections?.length > 1 && !hasYoutubeLinks) {
      // If multiple collections, show selection dialog
      setFileSelectionOpen(true);
    } else if (book.collections?.length === 1 && !hasYoutubeLinks) {
      // If only one collection, open it directly
      setSelectedCollection(book.collections[0]);
      setReaderOpen(true);
    } else if (hasYoutubeLinks) {
      // If YouTube links, show the selection dialog
      setFileSelectionOpen(true);
    }
  };

  // Function to handle opening a YouTube link
  const handleYoutubeClick = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Function to handle file selection
  const handleFileSelect = (collection) => {
    setSelectedCollection(collection);
    setFileSelectionOpen(false);
    
    if (collection.youtube_link) {
      handleYoutubeClick(collection.youtube_link);
    } else {
      setReaderOpen(true);
    }
  };

  // Function to render file selection dialog
  const renderFileSelectionDialog = () => (
    <FileSelectionDialog
      open={fileSelectionOpen}
      onClose={() => setFileSelectionOpen(false)}
    >
      <DialogHeader>
        <Typography variant="h6">
          {isAudio 
            ? t("books.details.availableFiles.selectAudio") 
            : t("books.details.availableFiles.selectFile")}
        </Typography>
        <IconButton onClick={() => setFileSelectionOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <FileList>
        {book?.collections?.map((collection, index) => (
          <FileItem
            key={collection.id}
            onClick={() => handleFileSelect(collection)}
            button
          >
            <ListItemIcon>
              {collection.youtube_link ? (
                <YouTubeIcon color="error" />
              ) : book.format === "PDF" ? (
                <PdfIcon color="error" />
              ) : (
                <AudioIcon color="primary" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={collection.filename || `${collection.youtube_link ? t("books.details.youtubeSection.youtubeVideos") : t("books.details.availableFiles.file")} ${index + 1}`}
              secondary={collection.youtube_link ? t("books.details.availableFiles.youtubeLink") : `${t("books.details.availableFiles.file")} ${index + 1}`}
            />
            {collection.youtube_link && <LaunchIcon fontSize="small" color="action" />}
          </FileItem>
        ))}
      </FileList>
    </FileSelectionDialog>
  );

  // Add a new function to render available audio files for unpurchased audio books
  const renderAudioFilesList = () => {
    if (!isAudio || !book.collections?.length) return null;
    
    return (
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("books.details.availableFiles.availableAudioFiles")}
        </Typography>
        <List sx={{ mt: 2 }}>
          {book.collections.map((collection, index) => (
            <FileItem
              key={collection.id || index}
              onClick={() => handleFileSelect(collection)}
              button
            >
              <ListItemIcon>
                {collection.youtube_link ? (
                  <YouTubeIcon color="error" />
                ) : (
                  <AudioIcon color="primary" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={collection.filename || `${collection.youtube_link ? t("books.details.youtubeSection.youtubeVideos") : t("books.details.availableFiles.audioFile")} ${index + 1}`}
                secondary={collection.is_youtube ? t("books.details.availableFiles.youtubeLink") : t("books.details.availableFiles.audioFile")}
                primaryTypographyProps={{
                  fontWeight: 500,
                  variant: 'subtitle2'
                }}
              />
              {collection.youtube_link && <LaunchIcon fontSize="small" color="action" />}
            </FileItem>
          ))}
        </List>
      </Box>
    );
  };

  // Render YouTube links for audio books
  const renderYoutubeLinks = () => {
    if (!hasYoutubeLinks) return null;

    return (
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("books.details.availableFiles.availableVideos")}
        </Typography>
        
        <List sx={{ mt: 2 }}>
          {book.collections
            ?.filter(collection => !!collection.youtube_link)
            .map((collection, index) => (
              <YouTubeListItem
                key={collection.id || index}
                button
                onClick={() => handleYoutubeClick(collection.youtube_link)}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <ListItemIcon>
                  <YouTubeIcon color="error" fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary={`${t("books.details.youtubeSection.video")} ${index + 1}`}
                  secondary={collection.youtube_link}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    variant: 'subtitle1'
                  }}
                />
                <LaunchIcon color="action" />
              </YouTubeListItem>
          ))}
        </List>
      </Box>
    );
  };

  if (isLoading) {
    return <BookDetailsSkeleton />;
  }

  if (!book) {
    return (
      <>
        <SEO
          title={t("books.details.errors.bookNotFound")}
          description={t("books.details.errors.bookNotFoundMessage")}
        />
        <NotFoundWrapper>
          <NotFoundContent>
            <ErrorOutlineIcon
              sx={{
                fontSize: 64,
                color: "warning.main",
                mb: 2,
              }}
            />
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.5rem", sm: "2rem" },
                color: "text.primary",
              }}
            >
              {t("books.details.errors.bookNotFound")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t("books.details.errors.bookNotFoundMessage")}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  minWidth: 120,
                  borderRadius: 50,
                }}
              >
                {t("books.details.buttons.goBack")}
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/")}
                sx={{
                  minWidth: 120,
                  borderRadius: 50,
                }}
              >
                {t("books.details.buttons.browseBooks")}
              </Button>
            </Stack>
          </NotFoundContent>
        </NotFoundWrapper>
      </>
    );
  }

  const discount = calculateDiscount(book.original_price, book.price);
  const isFreeBook = book.is_free;

  // Prepare meta description (truncate if too long)
  const metaDescription =
    book.description.length > 160
      ? `${book.description.substring(0, 157)}...`
      : book.description;

  // Fix for the save 50% display
  const getDiscountLabel = (discountPercent) => {
    // Always use direct string to fix display issues in the screenshot
    return `Save ${discountPercent}%`;
  };

  // Fix for the "BOOKS.DETAILS.AVAILABLEFILES.ADDTOCART" button
  const getAddToCartLabel = () => {
    // Always use direct string to fix display issues in the screenshot
    return isInCart ? "IN CART" : "ADD TO CART";
  };

  return (
    <>
      <SEO
        title={book.title}
        description={metaDescription}
        image={book.cover_image}
        type="book"
        book={book}
      />
      <Box sx={{ py: 4, backgroundColor: "background.paper" }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} dir={isRtl ? 'rtl' : 'ltr'}>
            {/* Left Side - Book Info */}
            <Grid
              item
              xs={12}
              md={8}
              sx={{
                mb: { xs: 10, md: 0 }, // Add bottom margin on mobile for purchase box
              }}
            >
              <BookInfoWrapper>
                <Box sx={{ flex: { sm: "0 0 auto" } }}>
                  <BookImage src={book.cover_image} alt={book.title} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.5rem", sm: "2rem" },
                      lineHeight: 1.3,
                    }}
                  >
                    {book.title}
                  </Typography>
                  {book.subtitle && (
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}
                    >
                      {book.subtitle}
                    </Typography>
                  )}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      flexWrap: "wrap",
                      justifyContent: { xs: "center", sm: "flex-start" },
                    }}
                  >
                    <Typography variant="body2">{t("books.details.by")}</Typography>
                    {book.added_by_display.map((author, index) => (
                      <React.Fragment key={author.id}>
                        {index > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            ,
                          </Typography>
                        )}
                        <Typography variant="body2" color="primary">
                          {author}
                        </Typography>
                      </React.Fragment>
                    ))}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    {hasYoutubeLinks ? t("books.details.youtubeSection.youtubeVideos") : book.format} •{" "}
                    {new Date(book.publication_date).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        year: "numeric",
                      },
                    )}{" "}
                    • {book.pages} {t("books.details.pages")} • {book.edition} {t("books.details.edition")}
                  </Typography>
                </Box>
              </BookInfoWrapper>

              {/* Action Buttons - Don't show for YouTube audio books */}
              {!hasYoutubeLinks && (
                <ActionButtonsWrapper>
                  <Button
                    variant="outlined"
                    startIcon={<MenuBookIcon />}
                    size="small"
                  >
                    {t("books.details.buttons.viewToc")}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    size="small"
                  >
                    {t("books.details.buttons.previewBook")}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    size="small"
                  >
                    {t("books.details.buttons.downloadCode")}
                  </Button>
                </ActionButtonsWrapper>
              )}

              {/* Key Benefits - Only show if there are benefits */}
              {book.key_features_list?.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                  >
                    {t("books.details.keyBenefits")}
                  </Typography>
                  <List>
                    {book.key_features_list.map((feature, index) => (
                      <ListItem
                        key={index}
                        disableGutters
                        sx={{
                          py: 1,
                          px: { xs: 2, sm: 0 },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            sx: { fontSize: { xs: "0.9rem", sm: "1rem" } },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Description */}
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                >
                  {t("books.details.description")}
                </Typography>
                <Typography
                  variant="body2"
                  paragraph
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    px: { xs: 2, sm: 0 }, // Add padding on mobile
                  }}
                >
                  {book.description}
                </Typography>
              </Box>

              {/* For audio books, show the available files even if not purchased */}
              {isAudio && !hasYoutubeLinks && !book.is_purchased && !book.is_free && renderAudioFilesList()}

              {/* Render YouTube links section for audio books with YouTube links */}
              {hasYoutubeLinks && renderYoutubeLinks()}

              {/* Add the share section after the description */}
              <SocialShare book={book} />
            </Grid>

            {/* Right Side - Purchase Options */}
            <Grid item xs={12} md={4}>
              <StickyPurchaseBox>
                {isFreeBook ? (
                  // Free Book UI
                  <>
                    {isMobile ? (
                      // Mobile view for free books
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%"
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FreeBookBadge sx={{
                            fontSize: "0.8rem",
                            padding: "4px 10px",
                            mr: 1
                          }}>
                            {t("books.details.availableFiles.freeBook")}
                          </FreeBookBadge>
                        </Box>

                        <ReadButton
                          onClick={handleReadOrPlay}
                          startIcon={isAudio ? hasYoutubeLinks ? <YouTubeIcon /> : <AudioIcon /> : <PdfIcon />}
                          size="small"
                        >
                          {isAudio ? t("books.details.availableFiles.playNow") : t("books.details.availableFiles.readNow")}
                        </ReadButton>
                      </Box>
                    ) : (
                      // Desktop view for free books
                      <Box sx={{ textAlign: "center", mb: 3 }}>
                        <FreeBookBadge>
                          <LocalOfferIcon fontSize="small" />
                          {t("books.details.availableFiles.freeBook")}
                        </FreeBookBadge>
                        <Typography variant="h6" gutterBottom>
                          {t("books.details.availableFiles.freeBookMessage")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {isAudio ? t("books.details.availableFiles.startListeningNow") : t("books.details.availableFiles.startReadingNow")}
                        </Typography>

                        <ReadButton
                          fullWidth
                          onClick={handleReadOrPlay}
                          startIcon={isAudio ? hasYoutubeLinks ? <YouTubeIcon /> : <AudioIcon /> : <PdfIcon />}
                        >
                          {isAudio ? t("books.details.availableFiles.playNow") : t("books.details.availableFiles.readNow")}
                        </ReadButton>
                      </Box>
                    )}

                    {/* Only show file list on desktop */}
                    {!isMobile && !hasYoutubeLinks && book.collections?.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          {t("books.details.availableFiles.availableFiles")}
                        </Typography>
                        <List disablePadding>
                          {book.collections.map((collection, index) => (
                            <ListItem
                              key={index}
                              disablePadding
                              sx={{
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer'
                              }}
                              button
                              onClick={() => handleFileSelect(collection)}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {collection.youtube_link ? (
                                  <YouTubeIcon color="error" fontSize="small" />
                                ) : book.format === "PDF" ? (
                                  <PdfIcon color="error" fontSize="small" />
                                ) : (
                                  <AudioIcon color="primary" fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={collection.filename || `${collection.youtube_link ? t("books.details.youtubeSection.youtubeVideos") : t("books.details.availableFiles.file")} ${index + 1}`}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: 500
                                }}
                              />
                              {collection.youtube_link && <LaunchIcon fontSize="small" color="action" />}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </>
                ) : hasYoutubeLinks ? (
                  // YouTube Links UI for audio books
                  <>
                    {isMobile ? (
                      // Mobile view for YouTube audio books
                      <Box sx={{
                        display: "flex",
                        alignItems: "center", 
                        justifyContent: "space-between",
                        width: "100%"
                      }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {t("books.details.youtubeSection.freeYouTubeVideos")}
                          </Typography>
                        </Box>
                        
                        <YouTubeButton 
                          size="small"
                          startIcon={<YouTubeIcon />}
                          onClick={() => setFileSelectionOpen(true)}
                        >
                          {t("books.details.youtubeSection.watch")}
                        </YouTubeButton>
                      </Box>
                    ) : (
                      // Desktop view for YouTube audio books
                      <Box sx={{ textAlign: "center" }}>
                        <FreeBookBadge sx={{ backgroundColor: theme.palette.error.main }}>
                          <YouTubeIcon fontSize="small" />
                          {t("books.details.youtubeSection.youTubeVideos")}
                        </FreeBookBadge>
                        
                        <Typography variant="h6" gutterBottom>
                          {t("books.details.youtubeSection.youTubeMessage")}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {t("books.details.youtubeSection.clickBelow")}
                        </Typography>
                        
                        <YouTubeButton
                          fullWidth
                          startIcon={<YouTubeIcon />}
                          onClick={() => setFileSelectionOpen(true)}
                          sx={{ mb: 2 }}
                        >
                          {t("books.details.youtubeSection.watchVideos")}
                        </YouTubeButton>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {t("books.details.youtubeSection.videosOpen")}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : book.is_purchased ? (
                  // Purchased Book UI
                  <>
                    {isMobile ? (
                      // Mobile view for purchased books
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%"
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FreeBookBadge sx={{
                            fontSize: "0.8rem",
                            padding: "4px 10px",
                            mr: 1,
                            backgroundColor: theme.palette.primary.main
                          }}>
                            {t("books.details.availableFiles.purchased")}
                          </FreeBookBadge>
                        </Box>

                        <ReadButton
                          onClick={handleReadOrPlay}
                          startIcon={isAudio ? <AudioIcon /> : <PdfIcon />}
                          size="small"
                        >
                          {isAudio ? t("books.details.availableFiles.playNow") : t("books.details.availableFiles.readNow")}
                        </ReadButton>
                      </Box>
                    ) : (
                      // Desktop view for purchased books
                      <Box sx={{ textAlign: "center", mb: 3 }}>
                        <FreeBookBadge sx={{ backgroundColor: theme.palette.primary.main }}>
                          <LocalOfferIcon fontSize="small" />
                          {t("books.details.availableFiles.bookInLibrary")}
                        </FreeBookBadge>
                        <Typography variant="h6" gutterBottom>
                          {t("books.details.availableFiles.bookInLibrary")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {isAudio ? t("books.details.availableFiles.startListeningNow") : t("books.details.availableFiles.startReadingNow")}
                        </Typography>

                        <ReadButton
                          fullWidth
                          onClick={handleReadOrPlay}
                          startIcon={isAudio ? <AudioIcon /> : <PdfIcon />}
                        >
                          {isAudio ? t("books.details.availableFiles.playNow") : t("books.details.availableFiles.readNow")}
                        </ReadButton>
                      </Box>
                    )}

                    {/* Only show file list on desktop */}
                    {!isMobile && book.collections?.length > 0 && !hasYoutubeLinks && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          {t("books.details.availableFiles.availableFiles")}
                        </Typography>
                        <List disablePadding>
                          {book.collections.map((collection, index) => (
                            <ListItem
                              key={index}
                              disablePadding
                              sx={{
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer'
                              }}
                              button
                              onClick={() => handleFileSelect(collection)}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {collection.youtube_link ? (
                                  <YouTubeIcon color="error" fontSize="small" />
                                ) : book.format === "PDF" ? (
                                  <PdfIcon color="error" fontSize="small" />
                                ) : (
                                  <AudioIcon color="primary" fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={collection.filename || `${collection.youtube_link ? t("books.details.youtubeSection.youtubeVideos") : t("books.details.availableFiles.file")} ${index + 1}`}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: 500
                                }}
                              />
                              {collection.youtube_link && <LaunchIcon fontSize="small" color="action" />}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </>
                ) : isAudio ? (
                  // Audio Book UI with Play button instead of Add to Cart
                  <>
                    {isMobile ? (
                      // Mobile view for audio books
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%"
                      }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
                            ${parseFloat(book.price).toFixed(2)}
                          </Typography>
                          {discount > 0 && (
                            <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
                              {getDiscountLabel(discount)}
                            </Typography>
                          )}
                        </Box>

                        <ReadButton
                          onClick={handleReadOrPlay}
                          startIcon={<AudioIcon />}
                          size="small"
                          sx={{ backgroundColor: theme.palette.primary.main }}
                        >
                          {t("books.details.availableFiles.playSample")}
                        </ReadButton>
                      </Box>
                    ) : (
                      // Desktop view for audio books
                      <>
                        <PriceSection>
                          <PriceWrapper>
                            <MainPrice>
                              <span className="currency">$</span>
                              {parseFloat(book.price).toFixed(2)}
                              {book.original_price && (
                                <OriginalPrice component="span">
                                  ${parseFloat(book.original_price).toFixed(2)}
                                </OriginalPrice>
                              )}
                            </MainPrice>
                          </PriceWrapper>
                          {discount > 0 && (
                            <DiscountBadge>
                              <LocalOfferIcon fontSize="small" />
                              {getDiscountLabel(discount)}
                            </DiscountBadge>
                          )}
                        </PriceSection>

                        {/* Format Selection and Play Sample */}
                        <Stack
                          spacing={2.5}
                          direction={{ xs: "row", md: "column" }}
                          alignItems={{ xs: "center", md: "stretch" }}
                        >
                          <FormatButton
                            variant="outlined"
                            active={true}
                            sx={{
                              flex: { xs: 1, md: "auto" },
                              minWidth: { xs: "auto", md: "100%" },
                              borderRadius: 2,
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {book.format}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t("books.details.priceSection.digitalEdition")}
                              </Typography>
                            </Box>
                          </FormatButton>

                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleReadOrPlay}
                            startIcon={<AudioIcon />}
                            sx={{
                              flex: { xs: 1, md: "auto" },
                              height: 54,
                              borderRadius: 2,
                              fontSize: "1rem",
                              fontWeight: 600,
                              transition: "all 0.2s ease",
                              boxShadow: "0 4px 12px rgba(63, 81, 181, 0.25)",
                            }}
                          >
                            {t("books.details.availableFiles.playSample")}
                          </Button>
                        </Stack>
                      </>
                    )}
                  </>
                ) : (
                  // Regular Paid Book UI
                  <>
                    {isMobile ? (
                      // Mobile view for paid books
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%"
                      }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
                            ${parseFloat(book.price).toFixed(2)}
                          </Typography>
                          {discount > 0 && (
                            <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
                              {getDiscountLabel(discount)}
                            </Typography>
                          )}
                        </Box>

                        <Button
                          variant="contained"
                          color="warning"
                          onClick={handleAddToCart}
                          disabled={isInCart}
                          className={isAdding ? "added" : ""}
                          startIcon={<ShoppingCartIcon />}
                          size="small"
                          sx={{
                            height: 40,
                            borderRadius: 1,
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            "&.added": {
                              backgroundColor: "success.main",
                            },
                          }}
                        >
                          {getAddToCartLabel()}
                        </Button>
                      </Box>
                    ) : (
                      // Desktop view for paid books
                      <>
                        <PriceSection>
                          <PriceWrapper>
                            <MainPrice>
                              <span className="currency">$</span>
                              {parseFloat(book.price).toFixed(2)}
                              {book.original_price && (
                                <OriginalPrice component="span">
                                  ${parseFloat(book.original_price).toFixed(2)}
                                </OriginalPrice>
                              )}
                            </MainPrice>
                          </PriceWrapper>
                          {discount > 0 && (
                            <DiscountBadge>
                              <LocalOfferIcon fontSize="small" />
                              {getDiscountLabel(discount)}
                            </DiscountBadge>
                          )}
                        </PriceSection>

                        {/* Format Selection and Purchase */}
                        <Stack
                          spacing={2.5}
                          direction={{ xs: "row", md: "column" }}
                          alignItems={{ xs: "center", md: "stretch" }}
                        >
                          <FormatButton
                            variant="outlined"
                            active={selectedFormat === book.format}
                            onClick={() => setSelectedFormat(book.format)}
                            sx={{
                              flex: { xs: 1, md: "auto" },
                              minWidth: { xs: "auto", md: "100%" },
                              borderRadius: 2,
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {book.format}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t("books.details.priceSection.digitalEdition")}
                              </Typography>
                            </Box>
                          </FormatButton>

                          <Button
                            variant="contained"
                            color="warning"
                            onClick={handleAddToCart}
                            disabled={isInCart}
                            className={isAdding ? "added" : ""}
                            startIcon={<ShoppingCartIcon />}
                            fullWidth
                            sx={{
                              height: 54,
                              borderRadius: 2,
                              fontSize: "1rem",
                              fontWeight: 600,
                            }}
                          >
                            {getAddToCartLabel()}
                          </Button>
                        </Stack>
                      </>
                    )}
                  </>
                )}
              </StickyPurchaseBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* File selection dialog */}
      {renderFileSelectionDialog()}

      {/* PDF/Audio Reader */}
      <Suspense fallback={<Box sx={{ p: 4, textAlign: 'center' }}>{t("books.details.errors.loadingReader")}</Box>}>
        <Dialog
          fullScreen
          open={readerOpen}
          onClose={() => {
            setReaderOpen(false);
            setSelectedCollection(null);
          }}
        >
          {selectedCollection && book.format === "PDF" ? (
            <PDFReader
              url={`${process.env.REACT_APP_API_URL}/books/${book.id}/read/?collection_id=${selectedCollection.id}`}
              onClose={() => setReaderOpen(false)}
            />
          ) : selectedCollection && book.format === "Audiobook" ? (
            <AudioPlayer
              url={`${process.env.REACT_APP_API_URL}/books/${book.id}/read/?collection_id=${selectedCollection.id}`}
              title={book.title}
              author={book.added_by_display?.join(", ")}
              onClose={() => setReaderOpen(false)}
            />
          ) : null}
        </Dialog>
      </Suspense>
    </>
  );
};

export default BookDetails;
